import React from 'react';
import { Button, Box } from '@mui/material';
import { AppColors } from '../theme/colors';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false
}) => {
  const getVisiblePages = () => {
    const maxVisiblePages = 5;
    const pages: number[] = [];
    
    if (totalPages <= maxVisiblePages) {
      // 総ページ数が5以下の場合は全て表示
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 常に5つのページを表示するように調整
      let startPage: number;
      let endPage: number;
      
      if (currentPage <= 3) {
        // 現在のページが3以下の場合は1から5まで表示
        startPage = 1;
        endPage = Math.min(5, totalPages);
      } else if (currentPage >= totalPages - 2) {
        // 現在のページが最後から3番目以降の場合は最後の5つを表示
        startPage = Math.max(1, totalPages - 4);
        endPage = totalPages;
      } else {
        // 現在のページを中心に5つのページを表示
        startPage = currentPage - 2;
        endPage = currentPage + 2;
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();

  if (totalPages <= 1) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* 最初のページボタン */}
      <Button
        onClick={() => onPageChange(1)}
        disabled={disabled || currentPage === 1}
        size="small"
        sx={{ 
          p: 1,
          borderRadius: '50%',
          minWidth: 32,
          height: 32,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: currentPage === 1 ? 'rgba(0, 0, 0, 0.26)' : 'rgba(0, 0, 0, 0.87)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        «
      </Button>
      
      {/* 前のページボタン */}
      <Button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={disabled || currentPage === 1}
        size="small"
        sx={{ 
          p: 1,
          borderRadius: '50%',
          minWidth: 32,
          height: 32,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: currentPage === 1 ? 'rgba(0, 0, 0, 0.26)' : 'rgba(0, 0, 0, 0.87)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        ‹
      </Button>
      
      {/* ページ番号ボタン */}
      {visiblePages.map((page) => {
        const isSelected = currentPage === page;
        return (
          <Button
            key={page}
            onClick={() => onPageChange(page)}
            disabled={disabled}
            size="small"
            sx={{ 
              p: 1,
              borderRadius: '50%',
              minWidth: 32,
              height: 32,
              fontSize: '0.875rem',
              fontWeight: 500,
              backgroundColor: isSelected ? AppColors.primary : 'transparent',
              color: isSelected ? 'white' : 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                backgroundColor: isSelected ? AppColors.primaryDark : 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            {page}
          </Button>
        );
      })}
      
      {/* 次のページボタン */}
      <Button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={disabled || currentPage === totalPages}
        size="small"
        sx={{ 
          p: 1,
          borderRadius: '50%',
          minWidth: 32,
          height: 32,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: currentPage === totalPages ? 'rgba(0, 0, 0, 0.26)' : 'rgba(0, 0, 0, 0.87)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        ›
      </Button>
      
      {/* 最後のページボタン */}
      <Button
        onClick={() => onPageChange(totalPages)}
        disabled={disabled || currentPage === totalPages}
        size="small"
        sx={{ 
          p: 1,
          borderRadius: '50%',
          minWidth: 32,
          height: 32,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: currentPage === totalPages ? 'rgba(0, 0, 0, 0.26)' : 'rgba(0, 0, 0, 0.87)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        »
      </Button>
    </Box>
  );
};
